/**
 * Layer placement for the Fabric editor.
 *
 * The mockup image is a shading map and sits ABOVE the design per the
 * template's layer order, so folds darken the artwork. For embroidery the
 * shading drops directly below the design (thread must not be darkened).
 * Layers above the design are HTML overlays on top of the Fabric canvas;
 * layers below it are painted under the canvas — the shading base as a
 * Fabric object, heathered/texture as <img> behind a transparent canvas
 * background.
 */

import { resolveMockupLayerOrder } from '@designdeck/shared';

export interface CanvasLayerPlacementInput {
  /** Stored template order, bottom → top */
  layerOrder: readonly string[] | null | undefined;
  /** Embroidery: the shading base moves directly below the design */
  shadingBelowDesign: boolean;
  /** A heathered overlay is available for the current colour */
  hasHeathered: boolean;
  /** A texture overlay is available */
  hasTexture: boolean;
  cutoutMaskUrl: string | null | undefined;
  /** Product colour painted into the Fabric background rect */
  backgroundColor: string;
}

export interface CanvasLayerPlacement {
  resolvedLayerOrder: string[];
  layersBelowDesign: string[];
  layersAboveDesign: string[];
  /** Shading base drawn by Fabric under the design objects (embroidery) */
  shadingInCanvas: boolean;
  /** Overlays that must show through the canvas from below it */
  overlaysBelowCanvas: string[];
  /**
   * Fill of the Fabric background rect. The wrapper already paints the
   * product colour when a cutout mask exists; the rect only doubles it and
   * must be transparent when an overlay sits under the canvas.
   */
  canvasBackgroundFill: string;
}

export function deriveCanvasLayerPlacement({
  layerOrder,
  shadingBelowDesign,
  hasHeathered,
  hasTexture,
  cutoutMaskUrl,
  backgroundColor,
}: CanvasLayerPlacementInput): CanvasLayerPlacement {
  const resolvedLayerOrder: string[] = resolveMockupLayerOrder(layerOrder, {
    shadingBelowDesign,
  });
  const designLayerIndex = resolvedLayerOrder.indexOf('design');
  const layersBelowDesign = resolvedLayerOrder.slice(0, designLayerIndex);
  const layersAboveDesign = resolvedLayerOrder.slice(designLayerIndex + 1);
  const shadingInCanvas = layersBelowDesign.includes('base');
  const overlaysBelowCanvas = layersBelowDesign.filter(
    (layer) =>
      (layer === 'heathered' && hasHeathered) ||
      (layer === 'texture' && hasTexture)
  );
  const canvasBackgroundFill =
    cutoutMaskUrl && overlaysBelowCanvas.length === 0
      ? backgroundColor
      : 'transparent';

  return {
    resolvedLayerOrder,
    layersBelowDesign,
    layersAboveDesign,
    shadingInCanvas,
    overlaysBelowCanvas,
    canvasBackgroundFill,
  };
}
